import { useMemo } from 'react';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ShipmentMetadata } from './shipment-metadata';
import { ChargesTable } from '@/components/common/charges-table';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import { SystemEntity } from '@erp/shared-types';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CircleNotchIcon, MapPinIcon } from '@phosphor-icons/react';
import { useChargesWithReversals } from '@/hooks/useChargesWithReversals';
import type { GetShipmentResponse } from '@erp/rpc';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import { SalesOrderDetails } from '@/components/accounts-receivable/sales-order-detail/sales-order-details';
import { T, useGT, Var } from 'gt-react';

type ShipmentWithDetails = NonNullable<GetShipmentResponse['shipment']>;

const EMPTY_DISPLAY = '-';

export interface ShipmentDetailsProps {
  shipment: ShipmentWithDetails;
  salesOrders?: GetShipmentResponse['salesOrders'];
  purchaseOrders?: GetShipmentResponse['purchaseOrders'];
  orderedDestinations?: GetShipmentResponse['orderedDestinations'];
  chargeBreakdowns?: GetShipmentResponse['chargeBreakdowns'];
  chargesCount?: number;
}

// Helper function to format currency
const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return EMPTY_DISPLAY;

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

/**
 * Fetches full sales order data and renders SalesOrderDetails.
 */
function SalesOrderSubTab({ salesOrderId }: { salesOrderId: string }) {
  const gt = useGT();
  const { data, isLoading, isError } = useQuery(
    orpc.accountsReceivable.salesOrders.get.queryOptions({
      input: { id: salesOrderId },
    }),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !data?.salesOrder) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {gt('Unable to load sales order details', {
          context: 'shipment detail tab error loading linked sales order',
        })}
      </div>
    );
  }

  return (
    <SalesOrderDetails
      salesOrder={data.salesOrder}
      lineItems={data.lineItems || []}
    />
  );
}

export function ShipmentDetails({
  shipment,
  salesOrders = [],
  purchaseOrders = [],
  orderedDestinations = [],
  chargeBreakdowns = [],
  chargesCount = 0,
}: ShipmentDetailsProps) {
  const gt = useGT();
  // Fetch charges for the shipment
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.SCM_SHIPMENT,
    entityId: shipment.id,
    includeLineCharges: false, // Only header charges
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );
  const { visibleCharges: visibleHeaderCharges } =
    useChargesWithReversals(headerCharges);
  const visibleChargeIds = useMemo(
    () => new Set(visibleHeaderCharges.map((charge) => charge.id)),
    [visibleHeaderCharges],
  );
  const visibleChargeBreakdowns = useMemo(
    () =>
      chargeBreakdowns.filter((breakdown) =>
        visibleChargeIds.has(breakdown.shipmentChargeId),
      ),
    [chargeBreakdowns, visibleChargeIds],
  );

  const hasSalesOrders = salesOrders.length > 0;
  const hasPurchaseOrders = purchaseOrders.length > 0;
  const hasCharges = visibleHeaderCharges.length > 0;
  const hasDestinations = orderedDestinations.length > 0;
  const hasTabs =
    hasSalesOrders || hasPurchaseOrders || hasCharges || hasDestinations;

  return (
    <div className="h-full flex flex-col space-y-4 overflow-y-auto px-3">
      <ShipmentMetadata shipment={shipment} chargesCount={chargesCount} />

      {/* Tabs for Related Entities and Charges */}
      {hasTabs && (
        <>
          <Separator />
          <Tabs
            defaultValue={
              hasSalesOrders
                ? `so-${salesOrders[0].id}`
                : hasPurchaseOrders
                  ? 'purchase-orders'
                  : hasDestinations
                    ? 'destinations'
                    : 'charges'
            }
            className="w-full"
          >
            <TabsList className="w-full">
              {salesOrders.map((so) => (
                <TabsTrigger key={so.id} value={`so-${so.id}`}>
                  <SystemEntityInline
                    entityType={SystemEntity.AR_SALES_ORDER}
                    entityId={so.id}
                  />
                </TabsTrigger>
              ))}
              {hasPurchaseOrders && (
                <TabsTrigger value="purchase-orders">
                  <T context="shipment detail tab, purchase orders with count">
                    Purchase Orders (<Var>{purchaseOrders.length}</Var>)
                  </T>
                </TabsTrigger>
              )}
              {hasDestinations && (
                <TabsTrigger value="destinations">
                  <T context="shipment detail tab, destinations with count">
                    Destinations (<Var>{orderedDestinations.length}</Var>)
                  </T>
                </TabsTrigger>
              )}
              {hasCharges && (
                <TabsTrigger value="charges">
                  <T context="shipment detail tab, charges with count">
                    Charges (<Var>{visibleHeaderCharges.length}</Var>)
                  </T>
                </TabsTrigger>
              )}
            </TabsList>

            {salesOrders.map((so) => (
              <TabsContent key={so.id} value={`so-${so.id}`} className="mt-0">
                <SalesOrderSubTab salesOrderId={so.id} />
              </TabsContent>
            ))}

            {hasPurchaseOrders && (
              <TabsContent value="purchase-orders" className="mt-0">
                <div className="space-y-2 p-3">
                  {purchaseOrders.map((po) => (
                    <div
                      key={po.id}
                      className="flex items-center justify-between p-2 rounded-md border"
                    >
                      <div className="flex items-center gap-3">
                        <SystemEntityInline
                          entityType={SystemEntity.INTERNAL_PURCHASE_ORDER}
                          entityId={po.id}
                        />
                        {po.supplierName && (
                          <span className="text-sm text-muted-foreground">
                            {po.supplierName}
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium">
                        {formatCurrencyValue(po.totalAmount)}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}

            {hasDestinations && (
              <TabsContent value="destinations" className="mt-0">
                <div className="space-y-2 p-3">
                  {orderedDestinations.map((destination) => (
                    <div
                      key={`${destination.sequence}-${destination.salesOrderId}`}
                      className="rounded-md border p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Badge variant="outline" className="text-xs">
                              <T context="shipment destination stop sequence number">
                                Stop <Var>{destination.sequence}</Var>
                              </T>
                            </Badge>
                            <span>{destination.destinationLabel}</span>
                          </div>
                          {destination.destinationAddress && (
                            <div className="flex items-start gap-1.5 text-xs text-muted-foreground whitespace-pre-wrap">
                              <MapPinIcon className="mt-0.5 h-3 w-3 shrink-0" />
                              <span>{destination.destinationAddress}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <SystemEntityInline
                            entityType={SystemEntity.AR_SALES_ORDER}
                            entityId={destination.salesOrderId}
                          />
                          {destination.customerName && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {destination.customerName}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            )}

            {hasCharges && (
              <TabsContent value="charges" className="mt-0">
                <div className="space-y-3 px-3">
                  <ChargesTable charges={visibleHeaderCharges} currency="USD" />
                  {visibleChargeBreakdowns.length > 0 && (
                    <div className="space-y-3 pb-3">
                      {visibleChargeBreakdowns.map((breakdown) => (
                        <Card key={breakdown.shipmentChargeId}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center justify-between gap-2">
                              <span>
                                <T context="shipment charge breakdown card title with category">
                                  <Var>
                                    {breakdown.categoryLabel ??
                                      gt('Charge', {
                                        context:
                                          'default label when charge category missing',
                                      })}
                                  </Var>{' '}
                                  allocations
                                </T>
                              </span>
                              <span className="text-muted-foreground font-medium">
                                {formatCurrencyValue(breakdown.amount)}
                              </span>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            {breakdown.allocations.map((allocation) => (
                              <div
                                key={`${breakdown.shipmentChargeId}-${allocation.salesOrderId}`}
                                className="flex items-center justify-between gap-3 rounded-md border p-2"
                              >
                                <div className="space-y-1">
                                  <SystemEntityInline
                                    entityType={SystemEntity.AR_SALES_ORDER}
                                    entityId={allocation.salesOrderId}
                                  />
                                  <div className="text-xs text-muted-foreground">
                                    {allocation.destinationLabel ??
                                      allocation.customerName ??
                                      gt('Unknown destination', {
                                        context:
                                          'shipment charge allocation when destination unknown',
                                      })}
                                  </div>
                                </div>
                                <div className="text-sm font-medium">
                                  {formatCurrencyValue(allocation.amount)}
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}
